// «Голос клиента» (#848) — о чём говорят клиенты по всему потоку, по
// адресатам решения. Pro и выше: флаг `enableVoiceOfCustomer`, проверяет его
// и сервер. Шлюз (тариф, выключатель, «не удалось узнать») спрашивает сам
// экран — `useFeatureGate` (#1231).
import { useCallback } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { MessagesSquare } from 'lucide-react';
import { DashboardBody } from '@/components/layout/DashboardBody';
import { Breadcrumb, BreadcrumbItem } from '@/components/navigation/breadcrumb';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { PageHelp } from '@/components/help/PageHelp';
import { useRole } from '@/contexts/RoleContext';
import { PLANS_HREF } from '@/lib/plans-href';
import { canAccessScreen } from '@/lib/route-permissions';
import { useOpenRecord } from '@/components/dashboards/shared/highlights/useOpenRecord';
import { VoiceOfCustomerView } from '@/components/analytics/voice/VoiceOfCustomerView';
import {
  parseVoiceSearch,
  type VoiceSearch,
} from '@/components/analytics/voice/voice-search';

function VoicePage() {
  const { t } = useTranslation('dashboard');
  useDocumentTitle(t('voicePage.title'));
  const { globalRole, isOwner } = useRole();
  // Billing is the account owner's screen: anyone else would land on a
  // settings page without it.
  const upgradeHref = canAccessScreen('billing', globalRole, isOwner)
    ? PLANS_HREF
    : undefined;
  const openRecord = useOpenRecord();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const onSearchChange = useCallback(
    (patch: Partial<VoiceSearch>) =>
      void navigate({ search: (previous) => ({ ...previous, ...patch }), replace: true }),
    [navigate]
  );
  const onOpenCall = useCallback(
    (recordId: string) => openRecord(recordId),
    [openRecord]
  );

  return (
    <DashboardBody
      title={
        <span className="flex items-center gap-2">
          {t('voicePage.title')}
          <PageHelp pageId="voiceOfCustomer" />
        </span>
      }
      subtitle={t('voicePage.subtitle')}
      icon={MessagesSquare}
      breadcrumbs={
        <Breadcrumb>
          <BreadcrumbItem href="/">Aelo</BreadcrumbItem>
          <BreadcrumbItem current>{t('voicePage.title')}</BreadcrumbItem>
        </Breadcrumb>
      }
    >
      <VoiceOfCustomerView
        search={search}
        onSearchChange={onSearchChange}
        onOpenCall={onOpenCall}
        upgradeHref={upgradeHref}
      />
    </DashboardBody>
  );
}

export const Route = createFileRoute('/analytics/voice')({
  component: VoicePage,
  validateSearch: (search: Record<string, unknown>): VoiceSearch =>
    parseVoiceSearch(search),
});
